import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import ImageUploader from "../components/ImageUploader";

const CATS = ["Hospitality", "Luxury Residential", "Commercial"];
const EMPTY = { title: "", category: "Hospitality", location: "", year: "", client: "", description: "", cover_image: "", images: [], featured: false };

export default function AdminProjects() {
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/projects").then((r) => setProjects(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      title: p.title, category: p.category, location: p.location || "", year: p.year || "",
      client: p.client || "", description: p.description || "", cover_image: p.cover_image,
      images: Array.isArray(p.images) ? p.images : [],
      featured: !!p.featured,
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.cover_image) {
      toast.error("Please upload a cover image.");
      return;
    }
    try {
      if (editing) {
        await api.put(`/projects/${editing.id}`, form);
        toast.success("Project updated.");
      } else {
        await api.post("/projects", form);
        toast.success("Project added.");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error("Failed to save.");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this project?")) return;
    await api.delete(`/projects/${id}`);
    toast.success("Deleted.");
    load();
  };

  const move = async (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= projects.length) return;
    // Optimistic UI update
    const next = [...projects];
    const [item] = next.splice(index, 1);
    next.splice(target, 0, item);
    setProjects(next);
    try {
      await api.put("/projects/reorder/all", { ids: next.map((p) => p.id) });
    } catch (err) {
      toast.error("Reorder failed.");
      load();
    }
  };

  return (
    <div className="p-8 md:p-12" data-testid="admin-projects-page">
      <div className="flex items-end justify-between mb-10 border-b border-borderWarm pb-6">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-muteInk">— Archive</p>
          <h1 className="font-serif text-4xl md:text-5xl text-ink mt-2 tracking-tight">Projects</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button
              onClick={openNew}
              data-testid="admin-new-project"
              className="border border-ink px-6 py-3 text-[12px] uppercase tracking-[0.22em] hover:bg-ink hover:text-bone transition-colors duration-300"
            >
              New project
            </button>
          </DialogTrigger>
          <DialogContent
            className="max-w-2xl max-h-[85vh] overflow-y-auto bg-bone border-borderWarm"
            data-lenis-prevent
          >
            <DialogHeader>
              <DialogTitle className="font-serif text-3xl text-ink">{editing ? "Edit project" : "New project"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={save} className="space-y-4">
              <F label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} required />
              <div>
                <label className="block text-[10px] uppercase tracking-[0.3em] text-muteInk mb-2">Category</label>
                <select
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="w-full bg-transparent border-b border-borderWarm py-2 text-ink outline-none"
                >
                  {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <F label="Location" value={form.location} onChange={(v) => setForm({ ...form, location: v })} />
                <F label="Year" value={form.year} onChange={(v) => setForm({ ...form, year: v })} />
              </div>
              <F label="Client" value={form.client} onChange={(v) => setForm({ ...form, client: v })} />
              <ImageUploader
                label="Cover image"
                value={form.cover_image}
                onChange={(v) => setForm({ ...form, cover_image: v })}
                testid="admin-cover-uploader"
              />
              <ImageUploader
                label="Gallery images"
                value={form.images}
                onChange={(v) => setForm({ ...form, images: v })}
                multiple
                testid="admin-gallery-uploader"
              />
              <div>
                <label className="block text-[10px] uppercase tracking-[0.3em] text-muteInk mb-2">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={3}
                  className="w-full bg-transparent border border-borderWarm p-3 text-ink outline-none focus:border-ink"
                />
              </div>
              <label className="flex items-center gap-3 text-[12px] uppercase tracking-[0.22em] text-ink">
                <input
                  type="checkbox"
                  checked={form.featured}
                  onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                  data-testid="admin-featured-toggle"
                />
                Featured on landing
              </label>
              <DialogFooter className="sticky bottom-0 bg-bone pt-4 -mx-6 px-6 border-t border-borderWarm">
                <button type="submit" data-testid="admin-save-project" className="border border-ink px-6 py-3 text-[12px] uppercase tracking-[0.22em] hover:bg-ink hover:text-bone transition-colors duration-300">
                  {editing ? "Save changes" : "Create project"}
                </button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-borderWarm">
        <div className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-borderWarm text-[10px] uppercase tracking-[0.28em] text-muteInk bg-stone2/30">
          <div className="col-span-1">Order</div>
          <div className="col-span-1">Cover</div>
          <div className="col-span-3">Title</div>
          <div className="col-span-2">Category</div>
          <div className="col-span-2">Location</div>
          <div className="col-span-1">Year</div>
          <div className="col-span-1">Feat.</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>
        {projects.map((p, idx) => (
          <div key={p.id} data-testid={`admin-project-row-${p.id}`} className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-borderWarm items-center hover:bg-stone2/20">
            <div className="col-span-1 flex flex-col gap-1">
              <button
                type="button"
                onClick={() => move(idx, -1)}
                disabled={idx === 0}
                data-testid={`admin-move-up-${p.id}`}
                className="w-7 h-6 border border-borderWarm hover:border-ink hover:bg-ink hover:text-bone disabled:opacity-25 disabled:hover:bg-transparent disabled:hover:text-inherit transition-colors text-xs"
                title="Move up"
              >↑</button>
              <button
                type="button"
                onClick={() => move(idx, 1)}
                disabled={idx === projects.length - 1}
                data-testid={`admin-move-down-${p.id}`}
                className="w-7 h-6 border border-borderWarm hover:border-ink hover:bg-ink hover:text-bone disabled:opacity-25 disabled:hover:bg-transparent disabled:hover:text-inherit transition-colors text-xs"
                title="Move down"
              >↓</button>
            </div>
            <div className="col-span-1"><img src={p.cover_image} alt="" className="w-14 h-14 object-cover" /></div>
            <div className="col-span-3 font-serif text-lg text-ink">{p.title}</div>
            <div className="col-span-2 text-sm text-ink/75">{p.category}</div>
            <div className="col-span-2 text-sm text-ink/75">{p.location}</div>
            <div className="col-span-1 text-sm text-ink/75">{p.year}</div>
            <div className="col-span-1 text-sm text-clay">{p.featured ? "★" : "—"}</div>
            <div className="col-span-1 flex justify-end gap-3 text-[11px] uppercase tracking-[0.22em]">
              <button onClick={() => openEdit(p)} className="editorial-link text-ink">Edit</button>
              <button onClick={() => del(p.id)} className="editorial-link text-clay">Del</button>
            </div>
          </div>
        ))}
        {projects.length === 0 && <div className="px-6 py-10 text-center text-muteInk">No projects yet.</div>}
      </div>
    </div>
  );
}

function F({ label, value, onChange, required }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.3em] text-muteInk mb-2">{label}</label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className="w-full bg-transparent border-b border-borderWarm py-2 text-ink outline-none focus:border-ink"
      />
    </div>
  );
}
